import { useState } from "react";
import { Archive, Trash2, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { errMsg } from "./LoadError";

const storeLabel = { lead: "Lead", investor: "Investor", contact: "Contact" };

function DepsSummary({ deps }) {
  const items = [
    deps.properties ? `${deps.properties} linked propert${deps.properties === 1 ? "y" : "ies"}` : "",
    deps.referrals ? `${deps.referrals} referral${deps.referrals === 1 ? "" : "s"}` : "",
    deps.conversions ? `${deps.conversions} recorded conversion${deps.conversions === 1 ? "" : "s"}` : "",
    deps.notes ? `${deps.notes} note${deps.notes === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  return (
    <div className="text-sm text-slate-600 space-y-2">
      {items.length > 0 ? (
        <p data-testid="lifecycle-deps">This record has {items.join(", ")}. These are <b>kept unchanged</b> — properties, deals, commissions and reporting history are never removed.</p>
      ) : <p data-testid="lifecycle-deps">No linked properties, referrals or conversions.</p>}
    </div>
  );
}

export function RecordLifecycle({ store, id, state = "active", onDone }) {
  const [mode, setMode] = useState(null);
  const [deps, setDeps] = useState(null);
  const [includeLinked, setIncludeLinked] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const open = async (m) => {
    setMode(m); setDeps(null); setReason("");
    try { const { data } = await api.get(`/crm/records/${store}/${id}/dependencies`); setDeps(data); setIncludeLinked(data.linked_people.length > 0 && data.linked_people.length <= 3); }
    catch (e) { toast.error(errMsg(e, "Could not check linked records")); setMode(null); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/records/${store}/${id}/${mode}`, { include_linked: includeLinked, reason });
      toast.success(mode === "archive" ? `Archived ${data.records.length} record(s)` : `Moved ${data.records.length} record(s) to Deleted`);
      setMode(null); onDone && onDone(mode);
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/crm/records/${store}/${id}/restore`); toast.success(`Restored ${data.records.length || 1} record(s)`); onDone && onDone("restore"); }
    catch (e) { toast.error(errMsg(e, "Could not restore")); }
    finally { setBusy(false); }
  };

  if (state !== "active") return (
    <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="record-inactive-banner">
      <div className="flex items-center gap-2 text-amber-200 text-sm"><ShieldAlert className="w-4 h-4" /> This record is <b className="capitalize">{state}</b> and hidden from active lists and follow-ups.</div>
      <button onClick={restore} disabled={busy} data-testid="record-restore" className="flex items-center gap-1.5 text-sm bg-brass text-emerald-950 font-semibold rounded-lg px-3 py-1.5"><RotateCcw className="w-4 h-4" /> Restore</button>
    </div>
  );

  return (
    <>
      <div className="flex gap-2">
        <button onClick={() => open("archive")} data-testid="record-archive" className="flex-1 flex items-center justify-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg py-2"><Archive className="w-4 h-4" /> Archive</button>
        <button onClick={() => open("delete")} data-testid="record-delete" className="flex-1 flex items-center justify-center gap-1.5 text-sm bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded-lg py-2"><Trash2 className="w-4 h-4" /> Delete</button>
      </div>
      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent data-testid="lifecycle-dialog">
          <DialogHeader>
            <DialogTitle>{mode === "archive" ? "Archive this record?" : "Delete this record?"}</DialogTitle>
            <DialogDescription>
              {mode === "archive" ? "It will disappear from active lists, pipelines and follow-up reminders. Notes and history are kept and you can restore it any time from the Archive page."
                : "It will be moved to Deleted records (soft delete). You can restore it, or permanently erase personal data later from the Archive page."}
            </DialogDescription>
          </DialogHeader>
          {!deps ? <p className="text-sm text-slate-500">Checking linked records…</p> : (
            <div className="space-y-3">
              <DepsSummary deps={deps} />
              {deps.linked_people.length > 0 && (
                <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3">
                  <input type="checkbox" checked={includeLinked} onChange={(e) => setIncludeLinked(e.target.checked)} data-testid="lifecycle-include-linked" className="mt-1" />
                  <span>Also {mode} this person's {deps.linked_people.length} other record(s) (same email, or same phone and name): {deps.linked_people.slice(0, 5).map((p) => `${storeLabel[p.store]} (${p.name})`).join(", ")}{deps.linked_people.length > 5 ? "…" : ""}</span>
                </label>
              )}
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional) — e.g. duplicate enquiry" data-testid="lifecycle-reason" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setMode(null)} className="text-sm px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">Cancel</button>
                <button onClick={confirm} disabled={busy} data-testid="lifecycle-confirm" className={`text-sm px-4 py-2 rounded-lg text-white font-semibold ${mode === "archive" ? "bg-emerald-800" : "bg-red-600"}`}>{busy ? "Working…" : mode === "archive" ? "Archive" : "Delete"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
